import { PageHelpProps } from './PageHelp';

// Scheduling Section
export const schedulingHelp: Record<string, PageHelpProps> = {
  zones: {
    title: 'Audio Zones',
    description: 'Zones represent distinct audio areas within your venues. Each zone can have its own schedule and playlist, allowing you to customize the music experience for different areas like the bar, dining room, or patio.',
    steps: [
      'Click "Add Zone" to create a new zone',
      'Assign the zone to a specific site/venue',
      'Configure the zone\'s default settings',
      'Link schedules to control what plays and when',
    ],
    tips: [
      { text: 'Name zones descriptively (e.g., "Main Bar", "Outdoor Patio")' },
      { text: 'Each zone can have multiple schedules for different days' },
      { text: 'Zones can be temporarily overridden without affecting the schedule' },
    ],
  },

  segments: {
    title: 'Music Segments',
    description: 'Segments are small, curated collections of 5-10 tracks designed for a specific mood or energy level. They\'re the building blocks for creating dynamic playlists that flow naturally throughout the day.',
    steps: [
      'Click "Add Segment" to create a new segment',
      'Set the target BPM range and energy level',
      'Add tracks from your content library',
      'Drag to reorder tracks within the segment',
    ],
    tips: [
      { text: 'Keep segments focused on one mood or energy level' },
      { text: '5-10 tracks per segment is ideal for variety without losing cohesion' },
      { text: 'Use BPM ranges to ensure smooth transitions between tracks' },
      { text: 'Click a segment card to manage its tracks' },
    ],
  },

  sequences: {
    title: 'Sequences',
    description: 'Sequences chain multiple segments together to create an "atmosphere arc" - a journey through different energy levels. Perfect for creating builds throughout service periods.',
    steps: [
      'Click "Add Sequence" to create a new sequence',
      'Add segments in the order you want them played',
      'Drag segments to reorder the playback sequence',
      'Preview the energy curve to see the flow',
    ],
    tips: [
      { text: 'Create "Dinner Service" sequences that build energy, peak, then wind down' },
      { text: 'The energy curve preview shows how mood changes over time' },
      { text: 'Click a sequence card to add and reorder segments' },
    ],
  },

  playlists: {
    title: 'Playlists',
    description: 'Playlists are traditional collections of tracks for straightforward scheduling. Use them when you want a simple shuffle without the energy-based transitions of sequences.',
    steps: [
      'Click "Add Playlist" to create a new playlist',
      'Search and add tracks from your content library',
      'Reorder tracks as needed',
      'Assign playlists to schedule entries',
    ],
    tips: [
      { text: 'Great for background music that doesn\'t need energy management' },
      { text: 'Click a playlist card to manage its tracks' },
      { text: 'Playlists can be shared across multiple zones' },
    ],
  },

  schedules: {
    title: 'Schedules',
    description: 'Schedules define what music plays in each zone throughout the week. Use the calendar view to assign playlists or sequences to specific time slots.',
    steps: [
      'Select a zone to view its schedule',
      'Click a time slot on the calendar to add an entry',
      'Choose a playlist or sequence for that time',
      'Use "Clone Day" to copy schedules across days',
    ],
    tips: [
      { text: 'Click a schedule card to open the full calendar view' },
      { text: 'Gaps in the schedule are highlighted - fill them to avoid silence' },
      { text: 'The calendar shows a full 24-hour view with scrolling' },
      { text: 'Use sequences for service periods, playlists for low-traffic times' },
    ],
  },

  channels: {
    title: 'Live Channels',
    description: 'Channels allow you to stream live audio from external sources like internet radio stations. Use them as alternatives to local playlists.',
    steps: [
      'Click "Add Channel" to create a new channel',
      'Enter the stream URL (HLS, Icecast, or Shoutcast)',
      'Test the stream connection',
      'Assign to schedules as an alternative to playlists',
    ],
    tips: [
      { text: 'Channels are great for live events or curated radio streams' },
      { text: 'Always test streams before scheduling them' },
      { text: 'Have a backup playlist in case the stream goes down' },
    ],
  },
};

// Fleet Section
export const fleetHelp: Record<string, PageHelpProps> = {
  overview: {
    title: 'Fleet Overview',
    description: 'The Fleet Overview provides a real-time dashboard of all your devices across all venues. Monitor device health, spot issues quickly, and see your entire deployment at a glance.',
    steps: [
      'Review the status cards for quick health metrics',
      'Use the map to see device locations',
      'Click on devices to see details',
      'Check for offline or error states',
    ],
    tips: [
      { text: 'Green = Online, Red = Offline, Orange = Error' },
      { text: 'The map updates in real-time with device status' },
      { text: 'Use filters to focus on specific organizations or sites' },
    ],
  },

  devices: {
    title: 'Device Management',
    description: 'Manage all your audio players from one place. View device status, configure settings, and troubleshoot issues remotely.',
    steps: [
      'Use search to find specific devices',
      'Filter by status to find problem devices',
      'Click a device row to see detailed information',
      'Use the action menu (⋮) for device operations',
    ],
    tips: [
      { text: 'Last seen time helps identify connectivity issues' },
      { text: 'Regenerate tokens if a device needs re-provisioning' },
      { text: 'Offline devices may need a power cycle or network check' },
    ],
  },
  playerHealth: {
    title: 'Player Health',
    description: 'Live status of every player app install, reported by the player itself every 45 seconds. Answer "is the player at this venue actually playing right now?" without calling the site.',
    steps: [
      'Check the summary cards: dead and stale players need attention first',
      'Filter by status or search by zone, site, organization or user',
      'Click a row to open the device timeline and error detail',
      'The page refreshes itself every 30 seconds',
    ],
    tips: [
      { text: 'Alive = heartbeat within 90 seconds, Stale = within 5 minutes, Dead = nothing since' },
      { text: 'A requirements warning means the device runs below the minimum device requirements' },
      { text: 'Errors (24h) counts player-side errors captured in the last day' },
      { text: 'Players not seen for 7 days are hidden and ignored by the status dots; use Show inactive to list them, or Remove Player to delete one' },
    ],
  },

  sites: {
    title: 'Sites',
    description: 'Sites represent physical locations like restaurants, hotels, or retail stores. Each site contains zones where music plays.',
    steps: [
      'Click "Add Site" to register a new location',
      'Enter the site address and coordinates',
      'Assign the site to an organization',
      'Add zones within each site',
    ],
    tips: [
      { text: 'Sites group devices and zones by physical location' },
      { text: 'GPS coordinates enable map-based monitoring' },
      { text: 'Each site can have multiple zones for different areas' },
    ],
  },

  organizations: {
    title: 'Organizations',
    description: 'Organizations are your top-level customers or business units. They contain sites, users, and all associated content and schedules.',
    steps: [
      'Click "Add Organization" to create a new customer',
      'Configure organization settings and limits',
      'Add sites under the organization',
      'Invite users to manage the organization',
    ],
    tips: [
      { text: 'Organizations enable multi-tenant isolation' },
      { text: 'Users can belong to multiple organizations' },
      { text: 'Content can be shared across or restricted to organizations' },
    ],
  },

  provisioning: {
    title: 'Device Provisioning',
    description: 'Provision new devices using QR codes. Generate codes for bulk deployment or on-demand activation at installation time.',
    steps: [
      'Select the organization and site for the device',
      'Choose pre-generated or on-demand QR code',
      'Print or display the QR code',
      'Scan with the device to complete provisioning',
    ],
    tips: [
      { text: 'Pre-generate codes for bulk hardware shipments' },
      { text: 'QR codes contain secure provisioning tokens' },
      { text: 'Devices automatically appear in the fleet once provisioned' },
    ],
  },

  alerts: {
    title: 'Alerts',
    description: 'Configure notifications for device issues. Get alerted via Slack or Google Chat when devices go offline or report errors.',
    steps: [
      'Click "Add Alert" to create a notification rule',
      'Select the alert trigger (offline, error, etc.)',
      'Configure the notification channel (Slack/Chat)',
      'Set cooldown period to prevent alert fatigue',
    ],
    tips: [
      { text: 'Use webhook URLs from Slack or Google Chat' },
      { text: 'Cooldown prevents repeated alerts for the same issue' },
      { text: 'Test alerts to verify webhook configuration' },
    ],
  },
};

// Management Section
export const managementHelp: Record<string, PageHelpProps> = {
  users: {
    title: 'User Management',
    description: 'Invite and manage users who can access the admin console. Control what each user can see and do through role assignments.',
    steps: [
      'Click "Invite User" to add a new user',
      'Enter their email and assign a role',
      'Copy and send the invite link',
      'User completes registration via the link',
    ],
    tips: [
      { text: 'Users can have different roles per organization' },
      { text: 'Pending users haven\'t completed their invite yet' },
      { text: 'Disabled users cannot log in but aren\'t deleted' },
      { text: 'Use the action menu to edit, disable, or resend invites' },
    ],
  },

  roles: {
    title: 'Roles & Permissions',
    description: 'Define what users can do within the system. Create custom roles or use the built-in roles to control access.',
    steps: [
      'Review built-in roles (Admin, Editor, Viewer)',
      'Click "Create Role" for custom permissions',
      'Select which permissions the role should have',
      'Assign the role to users via User Management',
    ],
    tips: [
      { text: 'Global Admin has full platform access' },
      { text: 'Organization roles are scoped to specific orgs' },
      { text: 'Lower priority numbers mean higher precedence' },
      { text: 'System roles cannot be deleted or renamed' },
    ],
  },

  auditLog: {
    title: 'Audit Log',
    description: 'Track all significant actions in the system for security and compliance. See who did what and when.',
    steps: [
      'Use filters to narrow down events',
      'Click rows to see full event details',
      'Export logs for compliance reporting',
      'Set date ranges for specific investigations',
    ],
    tips: [
      { text: 'All user actions are automatically logged' },
      { text: 'IP addresses help identify access patterns' },
      { text: 'Export to CSV for external analysis tools' },
      { text: 'Logs are retained for compliance requirements' },
    ],
  },
};

// Content Section
export const contentHelp: Record<string, PageHelpProps> = {
  library: {
    title: 'Content Library',
    description: 'Browse, search, and manage all your audio and video content. Preview files, edit metadata, and organize with tags.',
    steps: [
      'Use search to find specific tracks',
      'Filter by status, genre, or tags',
      'Click a row to preview the track',
      'Use the action menu to edit or delete',
    ],
    tips: [
      { text: 'Click the play button to preview audio' },
      { text: 'Processing status shows analysis progress' },
      { text: 'Tags help organize content for scheduling' },
      { text: 'Approved content is ready for scheduling' },
    ],
  },

  upload: {
    title: 'Upload Content',
    description: 'Add new audio or video files to your content library. Files are automatically processed to extract metadata.',
    steps: [
      'Drag files onto the upload area or click to browse',
      'Wait for upload to complete',
      'Files are automatically processed for BPM/loudness',
      'Review and tag content in the Content Library',
    ],
    tips: [
      { text: 'Supported: MP3, WAV, FLAC, AAC, OGG, M4A' },
      { text: 'Video: MP4, MOV, WebM, AVI, MKV' },
      { text: 'Max file size: 2GB' },
      { text: 'Processing extracts BPM, energy, and loudness' },
    ],
  },

  tags: {
    title: 'Content Tags',
    description: 'Create and manage tags to organize your content. Tags make it easy to filter and build smart playlists.',
    steps: [
      'Click "Add Tag" to create a new tag',
      'Choose a category and color',
      'Apply tags to content in the Content Library',
      'Use tags to filter when building playlists',
    ],
    tips: [
      { text: 'Use categories like Mood, Genre, Energy, Era' },
      { text: 'Colors help visually identify tag types' },
      { text: 'Tags can be applied to multiple tracks' },
      { text: 'Click a tag to see all content with that tag' },
    ],
  },
};

// Dashboard
export const dashboardHelp: PageHelpProps = {
  title: 'Dashboard',
  description: 'Your command center for the JustPlay platform. Get a quick overview of content, system health, and recent activity.',
  steps: [
    'Review stats cards for key metrics',
    'Check system health status',
    'Use quick actions for common tasks',
    'Click tiles to navigate to detailed pages',
  ],
  tips: [
    { text: 'Green indicators mean everything is healthy' },
    { text: 'Processing queue shows pending analysis jobs' },
    { text: 'Tiles are clickable for quick navigation' },
  ],
};
